import React, { useState, useCallback } from "react";
import { LoginForm } from "../components/LoginForm";
import { PasskeyChallenge } from "../components/PasskeyChallenge";
import { PasskeyEnroll } from "../components/PasskeyEnroll";
import { EmailCodeChallenge } from "../components/EmailCodeChallenge";
import { logoutCustomAuth } from "../auth/cognito-auth";
import { initiateCustomAuth, type AuthFlowState } from "../auth/cognito-auth";
import type { AssertionOptions, AuthenticationResult } from "../auth/webauthn";

interface LoginPageProps {
  onAuthenticated: () => void;
}

/**
 * LoginPage — Orchestrates the full custom auth flow.
 *
 * State machine:
 *   IDLE → AUTHENTICATING → PASSKEY_CHALLENGE → SUCCESS
 *                         → EMAIL_CODE (no passkey yet) → MUST_ENROLL → SUCCESS
 *                         → SUCCESS (device remembered)
 *                         → ERROR
 */
export const LoginPage: React.FC<LoginPageProps> = ({ onAuthenticated }) => {
  const [flowState, setFlowState] = useState<AuthFlowState>({ step: "IDLE" });
  // Signed in with the emailed code: a passkey must be enrolled before the app opens.
  const [mustEnroll, setMustEnroll] = useState(false);
  const [codeResolver, setCodeResolver] = useState<{
    resolve: (code: string) => void;
    reject: (err: Error) => void;
  } | null>(null);
  const [codeBusy, setCodeBusy] = useState(false);

  // Resolve function for the passkey challenge promise
  const [passkeyResolver, setPasskeyResolver] = useState<{
    resolve: (result: AuthenticationResult) => void;
    reject: (err: Error) => void;
  } | null>(null);

  const handleLogin = useCallback(
    async (email: string, password: string, remember: boolean) => {
      setFlowState({ step: "AUTHENTICATING" });
      let enrollFirst = false;

      try {
        await initiateCustomAuth(email, password, {
          rememberDevice: remember,
          onStateChange: (state) => {
            setFlowState(state);
          },
          onPasskeyChallenge: (opts: AssertionOptions) => {
            // Return a promise that the PasskeyChallenge component will resolve
            return new Promise<AuthenticationResult>((resolve, reject) => {
              setPasskeyResolver({ resolve, reject });
            });
          },
          onEmailCode: () => new Promise<string>((resolve, reject) => {
            setCodeBusy(false);
            setCodeResolver({ resolve, reject });
          }),
          onMustEnroll: () => { enrollFirst = true; setMustEnroll(true); },
        }).then(() => setCodeBusy(false), (e) => { setCodeBusy(false); throw e; });

        // Success — redirect to app (unless a passkey must be enrolled first)
        if (!enrollFirst) onAuthenticated();
      } catch (err: any) {
        if (err.message === "ENROLL_REQUIRED") {
          setFlowState({ step: "ENROLL_REQUIRED" });
        }
        // Other errors are handled via onStateChange
      }
    },
    [onAuthenticated]
  );

  const handlePasskeySuccess = useCallback(
    (result: AuthenticationResult) => {
      if (passkeyResolver) {
        passkeyResolver.resolve(result);
        setPasskeyResolver(null);
      }
    },
    [passkeyResolver]
  );

  const handlePasskeyError = useCallback(
    (error: string) => {
      if (passkeyResolver) {
        passkeyResolver.reject(new Error(error));
        setPasskeyResolver(null);
      }
    },
    [passkeyResolver]
  );

  const handlePasskeyCancel = useCallback(() => {
    if (passkeyResolver) {
      passkeyResolver.reject(new Error("Cancelled by user"));
      setPasskeyResolver(null);
    }
    setFlowState({ step: "IDLE" });
  }, [passkeyResolver]);

  const handleEnrolled = useCallback(() => {
    // After enrollment, go back to login form to retry auth
    setFlowState({ step: "IDLE" });
  }, []);

  // ─── Render based on state ───────────────────────────────────────────

  if (mustEnroll) {
    // Cancelling signs out: an account without a passkey only ever gets in
    // for long enough to enroll one.
    return (
      <PasskeyEnroll
        onEnrolled={onAuthenticated}
        onCancel={() => { logoutCustomAuth(); setMustEnroll(false); setFlowState({ step: "IDLE" }); }}
      />
    );
  }

  if (flowState.step === "EMAIL_CODE") {
    return (
      <EmailCodeChallenge
        emailHint={flowState.emailHint}
        busy={codeBusy}
        onSubmit={(code) => { setCodeBusy(true); codeResolver?.resolve(code); setCodeResolver(null); }}
        onCancel={() => { codeResolver?.reject(new Error("Cancelled by user")); setCodeResolver(null); setFlowState({ step: "IDLE" }); }}
      />
    );
  }

  if (flowState.step === "PASSKEY_CHALLENGE") {
    return (
      <PasskeyChallenge
        assertionOptions={flowState.assertionOptions}
        onSuccess={handlePasskeySuccess}
        onError={handlePasskeyError}
        onCancel={handlePasskeyCancel}
      />
    );
  }

  if (flowState.step === "ENROLL_REQUIRED") {
    return (
      <PasskeyEnroll
        onEnrolled={handleEnrolled}
        onCancel={() => setFlowState({ step: "IDLE" })}
      />
    );
  }

  return (
    <LoginForm
      onSubmit={handleLogin}
      isLoading={flowState.step === "AUTHENTICATING"}
      error={flowState.step === "ERROR" ? flowState.message : undefined}
    />
  );
};
